/**
 * Grant management for one subject — a space or a node (§4.5).
 *
 * The same component serves both, because a grant does not care which it sits
 * on: `subject_type` and `subject_id` are two columns, and the resolution walk
 * treats a node subject and a space subject as the same kind of ancestor. A
 * separate space-grants and node-grants screen would imply a distinction the
 * model does not have.
 *
 * Nothing here interprets a grant. A row is written, a row is deleted, and what
 * either means is resolved by the database and read back in the effective-access
 * views — which is the only place in this app that answers "so who can see it".
 */

import { useState } from 'react';
import { Form } from 'react-aria-components';
import { useCreateGrant, useGrants, usePeoplePicker, useRevokeGrant } from '../api/queries.ts';
import {
  type GrantEffect,
  type GrantSubject,
  isRolePrincipal,
  USER_ROLES,
  type UserRole,
} from '../api/types.ts';
import { CAPABILITY_OPTIONS, roleCarries } from './capabilities.ts';
import { formatDateTime } from './format.ts';
import { Badge, EffectBadge, roleLabel } from './ui/badge.tsx';
import { Button } from './ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from './ui/dialog.tsx';
import { Alert, ErrorState } from './ui/feedback.tsx';
import { Select, TextField } from './ui/field.tsx';
import { Card, CardHeader, Mono } from './ui/layout.tsx';
import { Table, TableEmpty, TBody, TD, TH, THead, TR } from './ui/table.tsx';

const EFFECT_OPTIONS = [
  { value: 'allow' as const, label: 'allow', description: 'Widens access at this subject' },
  {
    value: 'deny' as const,
    label: 'deny',
    description: 'Blocks here — but a nearer allow below still wins (§4.6)',
  },
];

const PRINCIPAL_OPTIONS = [
  {
    value: 'person' as const,
    label: 'A person',
    description: 'Beats a role grant at equal distance',
  },
  { value: 'role' as const, label: 'A role', description: 'Applies to everyone holding that role' },
];

const ROLE_PRINCIPALS = USER_ROLES.map((role) => ({ value: role, label: roleLabel(role) }));

function AddGrantForm({
  subjectType,
  subjectId,
  onDone,
}: {
  subjectType: GrantSubject;
  subjectId: string;
  onDone: () => void;
}) {
  const create = useCreateGrant();
  const picker = usePeoplePicker();

  const [principalType, setPrincipalType] = useState<'person' | 'role'>('person');
  const [principalId, setPrincipalId] = useState<string>('');
  const [capability, setCapability] = useState<string>('document.view');
  const [effect, setEffect] = useState<GrantEffect>('allow');
  const [reason, setReason] = useState('');
  const [noop, setNoop] = useState(false);

  const peopleOptions = (picker.data?.people ?? []).map((person) => ({
    value: person.id,
    label: person.name,
    description: `${person.email} · ${roleLabel(person.role)}`,
  }));

  // §4.4 — a capability outside the principal's role baseline resolves to
  // denied no matter what this row says, so writing it is inert. Worth saying
  // before the row exists rather than after someone wonders why it did nothing.
  const inertForRole =
    principalType === 'role' && principalId
      ? !roleCarries(principalId as UserRole, capability)
      : principalType === 'person' && principalId
        ? !roleCarries(
            (picker.data?.people ?? []).find((p) => p.id === principalId)?.role ?? 'portal_user',
            capability,
          )
        : false;

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate(
          {
            subjectType,
            subjectId,
            principalType,
            principalId,
            capability,
            effect,
            ...(reason.trim() ? { reason: reason.trim() } : {}),
          },
          {
            onSuccess: (result) => {
              if (result.created) onDone();
              else setNoop(true);
            },
          },
        );
      }}
    >
      <DialogBody>
        <Select
          label="Grant to"
          options={PRINCIPAL_OPTIONS}
          selectedKey={principalType}
          onSelectionChange={(value) => {
            setPrincipalType(value);
            setPrincipalId('');
          }}
        />
        {principalType === 'person' ? (
          <Select
            label="Person"
            placeholder="Choose a person…"
            options={peopleOptions}
            selectedKey={principalId || null}
            onSelectionChange={setPrincipalId}
            isRequired
          />
        ) : (
          <Select
            label="Role"
            placeholder="Choose a role…"
            options={ROLE_PRINCIPALS}
            selectedKey={principalId || null}
            onSelectionChange={setPrincipalId}
            isRequired
          />
        )}
        <Select
          label="Capability"
          options={CAPABILITY_OPTIONS}
          selectedKey={capability}
          onSelectionChange={setCapability}
        />
        <Select
          label="Effect"
          options={EFFECT_OPTIONS}
          selectedKey={effect}
          onSelectionChange={setEffect}
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={setReason}
          description="Recorded on the event."
        />

        {inertForRole ? (
          <Alert tone="warning" title="This grant would be inert">
            That role's baseline does not carry <span className="font-mono">{capability}</span>, and
            a role baseline is checked inside the resolver. The row would be written and would still
            resolve to denied.
          </Alert>
        ) : null}

        {effect === 'deny' ? (
          <Alert tone="info">
            A deny here does not settle the question below it. A person-specific allow closer to the
            node wins, which is the behaviour ADR 0003 accepted deliberately.
          </Alert>
        ) : null}

        {noop ? (
          <Alert tone="info" title="That grant already exists">
            §4.5 makes the exact tuple unique, so asking for a state that already holds is a no-op
            rather than a conflict.
          </Alert>
        ) : null}

        {create.isError ? <ErrorState error={create.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onDone}>
          Close
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={create.isPending}
          isDisabled={!principalId}
        >
          Write the grant
        </Button>
      </DialogFooter>
    </Form>
  );
}

export function GrantsPanel({
  subjectType,
  subjectId,
  subjectLabel,
}: {
  subjectType: GrantSubject;
  subjectId: string;
  subjectLabel: string;
}) {
  const grants = useGrants(subjectType, subjectId);
  const revoke = useRevokeGrant();
  const [adding, setAdding] = useState(false);

  return (
    <Card padded={false}>
      <CardHeader
        title="Grants on this subject"
        description={`Rows written directly against ${subjectType} ${subjectLabel}. Grants on ancestors reach here too and are not listed — the effective-access view is what resolves the whole chain.`}
        actions={
          <Button size="sm" variant="primary" onPress={() => setAdding(true)}>
            Add grant
          </Button>
        }
      />

      {grants.isError ? (
        <div className="p-5">
          <ErrorState error={grants.error} />
        </div>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Principal</TH>
              <TH>Capability</TH>
              <TH>Effect</TH>
              <TH>Granted</TH>
              <TH className="text-right">&nbsp;</TH>
            </TR>
          </THead>
          <TBody>
            {grants.isPending ? (
              <TableEmpty colSpan={5}>Loading…</TableEmpty>
            ) : grants.data.grants.length === 0 ? (
              <TableEmpty colSpan={5}>
                No grants written here. Anything reachable is reaching through an ancestor.
              </TableEmpty>
            ) : (
              grants.data.grants.map((grant) => (
                <TR key={grant.id} className="hover:bg-secondary">
                  <TD>
                    {isRolePrincipal(grant.principal) ? (
                      <span className="flex items-center gap-2">
                        <Badge tone="gray">role</Badge>
                        <span className="text-primary">
                          {roleLabel(grant.principal.role as UserRole)}
                        </span>
                      </span>
                    ) : (
                      <span className="flex flex-col">
                        <span className="flex items-center gap-2">
                          <Badge tone="brand">person</Badge>
                          <span className="font-medium text-primary">
                            {grant.principal.name ?? grant.principalId}
                          </span>
                        </span>
                        {grant.principal.email ? (
                          <span className="text-xs text-tertiary">{grant.principal.email}</span>
                        ) : null}
                      </span>
                    )}
                  </TD>
                  <TD>
                    <Badge tone="outline" mono>
                      {grant.capability}
                    </Badge>
                  </TD>
                  <TD>
                    <EffectBadge effect={grant.effect} />
                  </TD>
                  <TD className="whitespace-nowrap text-xs">
                    {formatDateTime(grant.createdAt)}
                    <Mono className="mt-0.5 block">{grant.id}</Mono>
                  </TD>
                  <TD className="text-right">
                    <Button
                      size="sm"
                      variant="tertiary"
                      className="text-error-primary"
                      isPending={revoke.isPending && revoke.variables?.id === grant.id}
                      onPress={() => revoke.mutate({ id: grant.id, subjectType, subjectId })}
                    >
                      Revoke
                    </Button>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      )}

      {revoke.isError ? (
        <div className="p-5">
          <ErrorState error={revoke.error} />
        </div>
      ) : null}

      <Dialog
        title="Add a grant"
        description={`On ${subjectType} ${subjectLabel}.`}
        isOpen={adding}
        onOpenChange={setAdding}
      >
        {(close) => <AddGrantForm subjectType={subjectType} subjectId={subjectId} onDone={close} />}
      </Dialog>
    </Card>
  );
}
